import { enableProdMode, ErrorHandler, importProvidersFrom, inject, Injectable, Injector, isDevMode, provideAppInitializer } from '@angular/core';
import { environment } from '@callcenter-env/environment';
import 'hammerjs';
import { HttpErrorResponse, HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  BASE_PATH,
  ComponentType,
  flattenObject,
  HX_APP_VERSION,
  HX_COMPONENT_NAME,
  HX_ENV_MODE,
  HX_GOOGLE_MAPS_API_KEY,
  HxError,
  initKeycloak,
  provideKeycloakFn,
  provideTranslocoFn,
  UiError
} from 'hx-services';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { APP_VERSION } from '@callcenter-env/version';
import { bootstrapApplication } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppComponent } from './app/app.component';
import { provideToastr, ToastrService } from 'ngx-toastr';
import { provideRouter, withRouterConfig } from '@angular/router';
import { ROUTES } from '@callcenter-app/app.routing';
import { TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/browser';
import { createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition, includeBearerTokenInterceptor } from 'keycloak-angular';
import { addI18n, HxToastrService } from 'hx-component';
import { provideNzI18n, ru_RU } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import { catchError } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import { throwError as observableThrowError } from 'rxjs';
import { addOrderI18n } from 'hx-order';

registerLocaleData(localeRu);

function getKeycloakUrl() {
  if (environment.production) {
    return location.origin.replace(location.origin.split('//')[1].split('.')[0], 'auth') + '/auth';
  }
  return environment.keycloakUrl;
}

if (environment.sentryUrl) {
  Sentry.init({
    release: environment.version,
    environment: environment.production ? 'production' : 'staging',
    ignoreErrors: ['Unauthorized', 'productBalance.zero'],
    dsn: environment.sentryUrl,
    sampleRate: 0,
    tracePropagationTargets: [],
    beforeSend: (event, hint) => {
      console.log('[sentry] before send', event, hint);
      if (hint && hint.originalException && hint.originalException instanceof HttpErrorResponse) {
        if (hint.originalException.status === 401) {
          console.log('[sentry] skipped error logging', event);
          return null;
        }
      }
      return event;
    },
  });
}

const customHttpInterceptorFn: HttpInterceptorFn = (req, next) => {
  const kc = inject(Keycloak);
  const toastr = inject(HxToastrService);
  const tr = inject(TranslocoService);
  let headers = req.headers.set('Accept-Language', tr.getActiveLang());
  if (isDevMode()) {
    headers = headers.set('component', ComponentType.cc);
  }
  return next(req.clone({headers: headers})).pipe(catchError(error => {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        kc.logout();
      }

      if (error.status === 502 || error.status === 504) {
        toastr.error(`Ошибка ${error.status} - Проблемы с сервером или интернетом (${req.url.split('/')[2]})`);
      }

      if (error.status === 500) {
        toastr.error(`Ошибка ${error.status} - ${error.statusText}`);
      }
    }

    return observableThrowError(error);
  }));
};

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {
  }

  handleError(error: any) {
    if (error.promise && error.rejection) {
      console.error('[got-rejection] %O', error);
      error = error.rejection;
    }
    const toastr = this.injector.get(ToastrService);
    const tr = this.injector.get(TranslocoService);
    if (error instanceof HttpErrorResponse) {
      const ex: HxError = error.error?.message ? error.error as HxError : {
        code: 577,
        id: '-',
        message: error.message,
        data: error.error ?? {},
      };
      toastr.error(ex.description ?? tr.translate(ex.message, flattenObject(ex.data)), tr.translate('error') + ' ' + ex.id);
    }
    if (error instanceof UiError) {
      toastr.error(tr.translate(error.message, error.data ?? {}));
    } else {
      console.error('[error-handler] %O', error);
    }
    Sentry.captureException(error.originalError || error);
  }
}

if (environment.production) {
  enableProdMode();
}
if (environment.production) {
  console.log = () => {};
}

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^\/api\/.*/,
  bearerPrefix: 'Bearer',
});

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(FormsModule, ReactiveFormsModule),
    provideRouter(ROUTES, withRouterConfig({
      // scrollPositionRestoration: 'disabled'
    })),
    provideTranslocoFn(),
    provideNzI18n(ru_RU),
    provideKeycloakFn({
      url: getKeycloakUrl(),
      realm: environment.keycloakRealm,
      clientId: environment.keycloakClientId,
    }),
    provideAppInitializer(() => {
      const tr = inject(TranslocoService);
      addI18n(tr);
      addOrderI18n(tr);
      return initKeycloak();
    }),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, customHttpInterceptorFn])),
    provideEnvironmentNgxMask(),
    provideAnimations(),
    provideToastr(),
    {
      provide: BASE_PATH,
      useValue: ''
    },
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition]
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: HX_COMPONENT_NAME,
      useValue: ComponentType.cc
    },
    {
      provide: HX_APP_VERSION,
      useValue: APP_VERSION
    },
    {
      provide: HX_GOOGLE_MAPS_API_KEY,
      useValue: environment.googleMapsApiKey
    },
    {provide: HX_ENV_MODE, useValue: environment.production},
  ]
});
